import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';

import { IUser } from '../../UserSlice';
import {
  adminCreateStudentSubscriptionRateVersion,
  adminDeleteStudentSubscriptionRateVersion,
  adminGetStudentSubscriptionRateVersions,
  adminGetStudentSubscriptionRateVersionsForDropDown,
  adminUpdateStudentSubscriptionRateVersion,
  adminVerifyStudentSubscriptionRateVersionDelete,
} from './adminApi';
import { RootState } from '../..';

export interface IStudentSubscriptionRateVersion {
  _id: string;
  name: string;
  createdBy: string | IUser;
  updatedBy: string | IUser;
}

export interface IStudentSubscriptionRateVersionDeleteVerificationData {
  studentSubscriptionRateCount: number;
}

export interface IInitialStudentSubscriptionRateVersionsState {
  status: 'idle' | 'loading' | 'rejected';
  // Admin
  adminStudentSubscriptionRateVersionListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IStudentSubscriptionRateVersion>;
  };
  adminStudentSubscriptionRateVersionListDataForDropDown?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IStudentSubscriptionRateVersion>;
  };
  adminStudentSubscriptionRateVersionDeleteVerificationData?: IStudentSubscriptionRateVersionDeleteVerificationData;
}

const initialState: IInitialStudentSubscriptionRateVersionsState = {
  status: 'idle',
  adminStudentSubscriptionRateVersionListData: undefined,
  adminStudentSubscriptionRateVersionListDataForDropDown: undefined,
  adminStudentSubscriptionRateVersionDeleteVerificationData: undefined,
};

export const StudentSubscriptionRateVersionsSlice = createSlice({
  initialState,
  name: 'studentSubscriptionRateVersionsSlice',
  reducers: {
    resetAdminStudentSubscriptionRateVersionListData: (state) => {
      state.adminStudentSubscriptionRateVersionListData = undefined;
    },
    resetAdminStudentSubscriptionRateVersionListDataForDropDown: (state) => {
      state.adminStudentSubscriptionRateVersionListDataForDropDown = undefined;
    },
    resetAdminStudentSubscriptionRateVersionDeleteVerificationData: (state) => {
      state.adminStudentSubscriptionRateVersionDeleteVerificationData = undefined;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(
        adminGetStudentSubscriptionRateVersions.fulfilled,
        (state, action) => {
          const result = action.payload;

          const newAdminStudentSubscriptionRateVersionListData = {
            limit: result.limit,
            offset: result.offset,
            totalDocs: result.totalDocs || 0,
            list: result.docs || [],
          };

          state.adminStudentSubscriptionRateVersionListData = newAdminStudentSubscriptionRateVersionListData;
        }
      )
      .addCase(
        adminGetStudentSubscriptionRateVersionsForDropDown.fulfilled,
        (state, action) => {
          const result = action.payload;

          const newAdminStudentSubscriptionRateVersionListDataForDropDown = {
            limit: result.limit,
            offset: result.offset,
            totalDocs: result.totalDocs || 0,
            list: result.docs || [],
          };

          state.adminStudentSubscriptionRateVersionListDataForDropDown = newAdminStudentSubscriptionRateVersionListDataForDropDown;
        }
      )
      .addCase(
        adminCreateStudentSubscriptionRateVersion.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (state.adminStudentSubscriptionRateVersionListData) {
            const newAdminStudentSubscriptionRateVersionListData = {
              ...state.adminStudentSubscriptionRateVersionListData,
            };

            newAdminStudentSubscriptionRateVersionListData.list.unshift(result);

            if (newAdminStudentSubscriptionRateVersionListData.totalDocs) {
              newAdminStudentSubscriptionRateVersionListData.totalDocs += 1;
            } else {
              newAdminStudentSubscriptionRateVersionListData.totalDocs = 1;
            }

            state.adminStudentSubscriptionRateVersionListData = newAdminStudentSubscriptionRateVersionListData;
          }
        }
      )
      .addCase(
        adminUpdateStudentSubscriptionRateVersion.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (
            state.adminStudentSubscriptionRateVersionListData &&
            state.adminStudentSubscriptionRateVersionListData.list &&
            state.adminStudentSubscriptionRateVersionListData.list.length
          ) {
            const newAdminStudentSubscriptionRateVersionListData = {
              ...state.adminStudentSubscriptionRateVersionListData,
            };

            const index = newAdminStudentSubscriptionRateVersionListData.list.findIndex(
              (item) => item._id === result._id
            );

            if (index > -1) {
              newAdminStudentSubscriptionRateVersionListData.list[
                index
              ] = result;
            }

            state.adminStudentSubscriptionRateVersionListData = newAdminStudentSubscriptionRateVersionListData;
          }
        }
      )
      .addCase(
        adminDeleteStudentSubscriptionRateVersion.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (
            state.adminStudentSubscriptionRateVersionListData &&
            state.adminStudentSubscriptionRateVersionListData.list &&
            state.adminStudentSubscriptionRateVersionListData.list.length
          ) {
            const newAdminStudentSubscriptionRateVersionListData = {
              ...state.adminStudentSubscriptionRateVersionListData,
            };

            newAdminStudentSubscriptionRateVersionListData.totalDocs = newAdminStudentSubscriptionRateVersionListData.totalDocs
              ? newAdminStudentSubscriptionRateVersionListData.totalDocs - 1
              : 0;

            newAdminStudentSubscriptionRateVersionListData.list = newAdminStudentSubscriptionRateVersionListData.list.filter(
              (item) => item._id !== result._id
            );

            state.adminStudentSubscriptionRateVersionListData = newAdminStudentSubscriptionRateVersionListData;
          }
        }
      )
      .addCase(
        adminVerifyStudentSubscriptionRateVersionDelete.fulfilled,
        (state, action) => {
          const result = action.payload;

          state.adminStudentSubscriptionRateVersionDeleteVerificationData = result;
        }
      );

    builder.addMatcher(
      isPending(
        adminCreateStudentSubscriptionRateVersion,
        adminGetStudentSubscriptionRateVersions,
        adminUpdateStudentSubscriptionRateVersion,
        adminDeleteStudentSubscriptionRateVersion,
        adminVerifyStudentSubscriptionRateVersionDelete
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isFulfilled(
        adminCreateStudentSubscriptionRateVersion,
        adminGetStudentSubscriptionRateVersions,
        adminUpdateStudentSubscriptionRateVersion,
        adminDeleteStudentSubscriptionRateVersion,
        adminVerifyStudentSubscriptionRateVersionDelete
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isRejected(
        adminCreateStudentSubscriptionRateVersion,
        adminGetStudentSubscriptionRateVersions,
        adminUpdateStudentSubscriptionRateVersion,
        adminDeleteStudentSubscriptionRateVersion,
        adminVerifyStudentSubscriptionRateVersionDelete
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getStudentSubscriptionRateVersionsState = (
  state: RootState
): RootState['studentSubscriptionRateVersions'] =>
  state.studentSubscriptionRateVersions;

export const {
  resetAdminStudentSubscriptionRateVersionListData,
  resetAdminStudentSubscriptionRateVersionListDataForDropDown,
  resetAdminStudentSubscriptionRateVersionDeleteVerificationData,
} = StudentSubscriptionRateVersionsSlice.actions;

export * from './adminApi';
export default StudentSubscriptionRateVersionsSlice.reducer;
